import classNames from 'classnames';
import { useSelect } from 'downshift';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import styles from './Autocomplete-Select.module.scss';

const MultiSelect = ({
  id,
  label,
  labelId,
  hideLabel,
  placeholder = 'Select any',
  onChange,
  items = [],
  classes = {},
  selectedItems = [],
  className,
  fieldName,
  iconOnlyAt,
  fullWidth = true,
  showWrapper = true,
  fillInput = true,
  isMenuListScroll = true,
  disabled,
  style = {},
  iconBefore,
  placeholderFunction,
  childrenPostMenuWrapper = () => {},
}) => {
  if (selectedItems.length > items.length) {
    const tempSelectedItems = items.filter((item) => selectedItems.filter((selectedItem) => isEqual(item.value, selectedItem.value)));
    onChange(tempSelectedItems, fieldName);
  }
  const addSelectedItem = (item) =>
    onChange([...selectedItems, item].map((s) => items.filter((i) => isEqual(i.value, s.value))).flat(1), fieldName);

  const removeSelectedItem = (item) => {
    const i = selectedItems.map((val) => val.value).indexOf(item.value);
    if (i >= 0) {
      const tempItems = [...selectedItems];
      tempItems.splice(i, 1);
      onChange(tempItems, fieldName);
    }
  };
  const selectedValues = selectedItems.map((s) => s.value);
  const selectedDisplays = selectedItems.map((s) => s.display);
  const args = useSelect({
    items,
    labelId: labelId,
    stateReducer: (state, { type, props, changes }) => {
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          if (changes.selectedItem) {
            if (selectedValues.includes(changes.selectedItem.value)) {
              setTimeout(() => removeSelectedItem(changes.selectedItem), 0);
            } else {
              setTimeout(() => addSelectedItem(changes.selectedItem), 0);
            }
          }
          return {
            ...state,
            ...changes,
            isOpen: true,
            highlightedIndex: state.highlightedIndex,
            selectedItem: changes.selectedItem,
          };
        case useSelect.stateChangeTypes.keyDownEscape: // don't clear input & don't clear selectedItem
          return {
            ...changes,
            selectedItem: state.selectedItem,
          };
        default:
          return { ...state, ...changes };
      }
    },
  });
  const { isOpen, getToggleButtonProps, getLabelProps, getMenuProps, highlightedIndex, getItemProps } = args;
  const isChecked = ({ item }) => {
    const { value, display } = item;
    if (Array.isArray(value)) {
      return selectedDisplays.includes(display);
    }
    return selectedValues.includes(value);
  };

  return (
    <div className={classNames(styles['shared-select'], className)} {...(id ? { id: id + '-container' } : {})}>
      {labelId ? (
        getLabelProps({ id: labelId, htmlFor: id }) && undefined
      ) : (
        <label
          style={{ ...style.label, ...(hideLabel ? { display: 'none' } : {}) }}
          {...getLabelProps({ htmlFor: id })}
          className={classNames(styles['label'], classes.label)}
        >
          {label}
        </label>
      )}

      <div
        className={classNames(
          styles['menu-wrapper'],
          { [styles['menu-wrapper--wrapping']]: showWrapper, [styles['menu-wrapper--fill-input']]: fillInput },
          classes.menuWrapper,
          {
            [classNames(styles['open'], classes.open)]: isOpen && items?.length,
          },
        )}
      >
        <div
          className={classNames(styles['autocomplete__input-wrapper'], classes.inputWrapper, {
            [classNames(styles['open'], classes.open)]: isOpen,
          })}
        >
          <button
            type="button"
            style={style.input}
            disabled={disabled}
            className={classNames(styles['select-toggle'], classes.select, classes.input, {
              [styles['selected']]: selectedItems.length > 0,
              [styles['full-width']]: fullWidth,
              [classNames(styles['open'], classes.open)]: isOpen && items.length,
            })}
            {...getToggleButtonProps({
              ...(id ? { id } : {}),
              tabIndex: '0',
            })}
          >
            {iconBefore && (
              <span style={style.iconBefore} className={classNames({ [styles['icon']]: !!label }, classes.iconBefore)}>
                {iconBefore}
              </span>
            )}
            <span
              className={classNames(styles['button-label'], classes.buttonLabel, {
                [`hide--${iconOnlyAt}`]: !!iconOnlyAt,
              })}
            >
              {placeholderFunction
                ? placeholderFunction(selectedItems)
                : selectedItems.length === 0
                ? placeholder
                : selectedItems.length === 1
                ? selectedItems[0].display
                : `${selectedItems.length} selected`}
            </span>
          </button>
        </div>

        <div
          className={classNames(styles['menu'], classes.menu, {
            [classNames(styles['open'], classes.open)]: isOpen && items?.length,
          })}
        >
          <ul
            className={classNames(styles['menu-list'], isMenuListScroll && styles['menu-scroll-container'], classes.menuList, {
              [classNames(styles['open'], classes.open)]: isOpen && items?.length,
            })}
            {...getMenuProps({ ...(id ? { id: `${id}-menu` } : {}) })}
          >
            {isOpen &&
              items?.map((item, index) => {
                const isSelected = isChecked({ item });
                return (
                  <li
                    key={`li-${index}`}
                    className={classNames(styles['menu-item'], styles['clickable-menu-item'], classes.menuItem, item.className, {
                      [classNames(styles['highlighted'], classes.highlighted)]: highlightedIndex === index,
                    })}
                    {...getItemProps({
                      key: index,
                      index,
                      item,
                      ...(id ? { id: `${id}-item-${index}` } : {}),
                    })}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <input type="checkbox" readOnly tabIndex="-1" checked={isSelected} />
                    {item.display}
                  </li>
                );
              })}
          </ul>
        </div>
      </div>
      {childrenPostMenuWrapper(args)}
    </div>
  );
};

MultiSelect.propTypes = {
  id: PropTypes.string,
  label: function (props, propName, componentName) {
    if (!props.labelId && !props.label) {
      return new Error(`One of 'label' or 'labelId' is required by '${componentName}' component.`);
    }
    if (props.labelId) {
      PropTypes.checkPropTypes(
        {
          labelId: PropTypes.string, // or any other PropTypes you want
        },
        props,
        propName,
        componentName,
      );
    }
    return null;
  },
  labelId: PropTypes.string,
  hideLabel: PropTypes.bool,
  fieldName: PropTypes.string,
  placeholder: PropTypes.string,
  selectValue: PropTypes.object,
  fullWidth: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      display: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      className: PropTypes.string,
    }),
  ),
  initialIndexes: PropTypes.arrayOf(PropTypes.number),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      display: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      className: PropTypes.string,
    }),
  ).isRequired,
  classes: PropTypes.shape({
    menuWrapper: PropTypes.string,
    open: PropTypes.string,
    select: PropTypes.string,
    menu: PropTypes.string,
    menuList: PropTypes.string,
    menuItem: PropTypes.string,
    label: PropTypes.string,
    highlighted: PropTypes.string,
  }),
  style: PropTypes.object,
  className: PropTypes.string,
  showWrapper: PropTypes.bool,
  fillInput: PropTypes.bool,
  placeholderFunction: PropTypes.func,
  isMenuListScroll: PropTypes.bool,
  disabled: PropTypes.bool,
  childrenPostMenuWrapper: PropTypes.func,
};

export { MultiSelect as default, MultiSelect };
